import { find } from "lodash"
import type { ResponsiveImageType, ImageVariant } from "../components"

/**
 * Get a specific image variant by style name
 * @param image - ResponsiveImageType object with variants array
 * @param styleName - The style name to find (e.g., "thumbnail_100x100_no_conversion")
 * @returns ImageVariant if found, null otherwise
 */
export const getImageVariantByStyle = (
  image: ResponsiveImageType | undefined,
  styleName: string
): ImageVariant | null => {
  if (!image?.variants) return null
  return find(image.variants, { style: styleName }) || null
}

/**
 * Create a ResponsiveImageType with a single variant as the default
 * Useful for forcing ResponsiveImage component to use a specific variant
 * @param image - ResponsiveImageType object with variants array  
 * @param styleName - The style name to use as the default
 * @returns ResponsiveImageType with the specified variant as default, or original image if variant not found
 */
export const createImageWithVariantAsDefault = (
  image: ResponsiveImageType | undefined,
  styleName: string
): ResponsiveImageType | undefined => {
  if (!image?.variants) return image
  
  const variant = getImageVariantByStyle(image, styleName)
  if (!variant) return image
  
  return {
    variants: [
      {
        ...variant,
        style: "default"
      }
    ]
  }
}

/**
 * Get the URL of a specific image variant by style name
 * @param image - ResponsiveImageType object with variants array
 * @param styleName - The style name to find
 * @returns URL string if found, empty string otherwise
 */
export const getImageVariantUrl = (
  image: ResponsiveImageType | undefined,
  styleName: string
): string => {
  const variant = getImageVariantByStyle(image, styleName)
  return variant?.url || ""
} 